import getTruthyKeys from '../utils/getTruthyKeys';
import { platforms } from '../utils/getPlatformClass';

type VisualizationOption = {
    value: string;
    name: string;
    queryValues?: string[]; // This property is specific to visualizationOptions
    array?: string[]; // This property is specific to visualizationOptionsFollowers
};

type filterOptions = {
    name: string;
    value: string;
};

export const socialMetricOptions: filterOptions[] = [
    {
        name: 'Post Volume',
        value: 'post',
    },
    {
        name: 'Engagements',
        value: 'engagement',
    },
    {
        name: 'Followers',
        value: 'follower',
    },
];

export const datePeriodicityOptions: filterOptions[] = [
    {
        name: 'Weekly',
        value: 'weekly',
    },
    {
        name: 'Monthly',
        value: 'monthly',
    },
    {
        name: 'Custom',
        value: 'custom',
    },
];

export const platformOptions = Object.entries(platforms).reduce(
    (acc, [key, value]) => {
        acc.push({
            value: parseInt(key),
            label: value,
        });
        return acc;
    },
    [],
);

export const allAccountTypeOptions = [
    {
        value: 3,
        label: 'Athlete',
    },
    {
        value: 1,
        label: 'Team',
    },
    {
        value: 2,
        label: 'League',
    },
];

// The name for the last element is assigned dinamically
// depending of the socialMetricValue

export const visualizationOptions: VisualizationOption[] = [
    {
        value: 'platform',
        name: 'Group By Platform',
        queryValues: ['accountGroup', 'platform'],
    },
    {
        value: 'mediaType',
        name: 'Group by Media Type',
        queryValues: ['accountGroup', 'mediaType'],
    },
    {
        value: 'engagementRate',
        name: 'Engagement Rate',
        queryValues: ['accountGroup'],
    },
    {
        value: 'total',
        name: '',
        queryValues: ['accountGroup'],
    },
];

export const visualizationOptionsFollowers: VisualizationOption[] = [
    {
        value: 'platform',
        name: 'Group By Platform',
        array: ['accountGroup', 'platform'],
    },
    {
        value: 'engagementRate',
        name: 'Engagement Rate',
        array: ['accountGroup'],
    },
    {
        value: 'total',
        name: '',
        array: ['accountGroup'],
    },
];

export const totalNames = {
    post: 'Total & Change Over Time',
    engagement: 'Total & Per Post',
    follower: 'Total & New Followers',
};
/**
 * From a list of options and the selected values in a Select Components
 * returns a string of labels selected separated by comma.
 */
export function selectedOptionsToString(selectedOptions, options) {
    const optionsMap = options.reduce((acc, opt) => {
        acc[opt.value] = opt.label;
        return acc;
    }, {});
    const keys = getTruthyKeys(selectedOptions);

    if (!keys || !keys.length) {
        return Object.values(optionsMap).join(', ');
    }

    return keys
        .map(id => {
            return optionsMap[id];
        })
        .join(', ');
}

/**
 * From a list of options and the selected values in a Select Component
 * returns an array of integers consisting of the equivalent selected values.
 * If nothing is selected, returns an array of integers consisting of
 * every option's value.
 */
export function selectedOptionsToArray(selectedOptions, options) {
    const optionsMap = options.reduce((acc, opt) => {
        acc[opt.value] = opt.label;
        return acc;
    }, {});

    const keys = getTruthyKeys(selectedOptions);

    if (!keys || !keys.length) {
        return Object.keys(optionsMap);
    }

    return keys;
}

/**
 * Translation helper functions for v2 components
 * These functions return translated versions of filter options
 * while keeping original exports unchanged for v1 compatibility
 */

export const getTranslatedSocialMetricOptions = (
    t: (key: string, options?: { ns?: string }) => string,
): filterOptions[] => {
    return [
        {
            name: t(
                'socialBenchmarking.filters.options.socialMetrics.postVolume',
                {
                    ns: 'benchmarking',
                },
            ),
            value: 'post',
        },
        {
            name: t(
                'socialBenchmarking.filters.options.socialMetrics.engagements',
                {
                    ns: 'benchmarking',
                },
            ),
            value: 'engagement',
        },
        {
            name: t(
                'socialBenchmarking.filters.options.socialMetrics.followers',
                {
                    ns: 'benchmarking',
                },
            ),
            value: 'follower',
        },
    ];
};

export const getTranslatedDatePeriodicityOptions = (
    t: (key: string, options?: { ns?: string }) => string,
): filterOptions[] => {
    return [
        {
            name: t(
                'socialBenchmarking.filters.options.datePeriodicity.weekly',
                {
                    ns: 'benchmarking',
                },
            ),
            value: 'weekly',
        },
        {
            name: t(
                'socialBenchmarking.filters.options.datePeriodicity.monthly',
                {
                    ns: 'benchmarking',
                },
            ),
            value: 'monthly',
        },
        {
            name: t(
                'socialBenchmarking.filters.options.datePeriodicity.custom',
                {
                    ns: 'benchmarking',
                },
            ),
            value: 'custom',
        },
    ];
};

export const getTranslatedVisualizationOptions = (
    t: (key: string, options?: { ns?: string }) => string,
): VisualizationOption[] => {
    return [
        {
            value: 'platform',
            name: t(
                'socialBenchmarking.filters.options.visualization.groupByPlatform',
                {
                    ns: 'benchmarking',
                },
            ),
            queryValues: ['accountGroup', 'platform'],
        },
        {
            value: 'mediaType',
            name: t(
                'socialBenchmarking.filters.options.visualization.groupByMediaType',
                {
                    ns: 'benchmarking',
                },
            ),
            queryValues: ['accountGroup', 'mediaType'],
        },
        {
            value: 'engagementRate',
            name: t(
                'socialBenchmarking.filters.options.visualization.engagementRate',
                {
                    ns: 'benchmarking',
                },
            ),
            queryValues: ['accountGroup'],
        },
        {
            value: 'total',
            name: '',
            queryValues: ['accountGroup'],
        },
    ];
};

export const getTranslatedVisualizationOptionsFollowers = (
    t: (key: string, options?: { ns?: string }) => string,
): VisualizationOption[] => {
    return [
        {
            value: 'platform',
            name: t(
                'socialBenchmarking.filters.options.visualization.groupByPlatform',
                {
                    ns: 'benchmarking',
                },
            ),
            array: ['accountGroup', 'platform'],
        },
        {
            value: 'engagementRate',
            name: t(
                'socialBenchmarking.filters.options.visualization.engagementRate',
                {
                    ns: 'benchmarking',
                },
            ),
            array: ['accountGroup'],
        },
        {
            value: 'total',
            name: '',
            array: ['accountGroup'],
        },
    ];
};

export const getTranslatedTotalNames = (
    t: (key: string, options?: { ns?: string }) => string,
) => {
    return {
        post: t('socialBenchmarking.filters.options.totalNames.post', {
            ns: 'benchmarking',
        }),
        engagement: t(
            'socialBenchmarking.filters.options.totalNames.engagement',
            {
                ns: 'benchmarking',
            },
        ),
        follower: t('socialBenchmarking.filters.options.totalNames.follower', {
            ns: 'benchmarking',
        }),
    };
};

export const getTranslatedAllAccountTypeOptions = (
    t: (key: string, options?: { ns?: string }) => string,
) => {
    return [
        {
            value: 3,
            label: t('socialBenchmarking.filters.options.accountTypes.athlete'),
        },
        {
            value: 1,
            label: t('socialBenchmarking.filters.options.accountTypes.team'),
        },
        {
            value: 2,
            label: t('socialBenchmarking.filters.options.accountTypes.league'),
        },
    ];
};
